// client/src/services/addressCleanse.ts
// API calls for the Address Validator feature.
//
// The cleanse call is read-only. Pushing to PE reuses the existing
// `update-client-record` import use case via executeImport(), so there is no
// separate write endpoint to keep in sync.

/** PE contact fields this tool cleanses. Keys match clientRecordImport.js. */
export const CLEANSE_FIELDS = [
  { key: 'ContAddress',  label: 'Street' },
  { key: 'ContTownCity', label: 'City' },
  { key: 'ContCounty',   label: 'State' },
  { key: 'ContPostCode', label: 'Zip' },
  { key: 'ContPhone',    label: 'Phone' },
  { key: 'ContMobile',   label: 'Mobile' },
  { key: 'ContEmail',    label: 'Email' },
] as const;

export type CleanseFieldKey = typeof CLEANSE_FIELDS[number]['key'];

export interface CleansedField {
  label: string;
  before: string;
  after: string;
  changed: boolean;
  /** Set when the USPS lookup overrode our locally-cleansed value. */
  uspsCorrected?: boolean;
  /** What the local rules produced, when USPS then changed it. */
  localAfter?: string;
}

export type UspsStatus =
  | 'disabled' | 'skipped' | 'verified' | 'missing_unit' | 'unit_unconfirmed'
  | 'undeliverable' | 'unverified' | 'not_found' | 'error' | 'quota';

export interface UspsResult {
  status: UspsStatus;
  dpv?: string | null;
  carrierRoute?: string | null;
  reason?: string;
  error?: string;
  standardized?: Partial<Record<CleanseFieldKey, string>>;
  corrections?: string[];
}

export interface CleanseWarning {
  field: string;
  label: string;
  message: string;
}

export interface CleansedRow {
  row: number;
  clientCode: string;
  clientName: string;
  fields: Partial<Record<CleanseFieldKey, CleansedField>>;
  changed: boolean;
  changeCount: number;
  warnings: CleanseWarning[];
  usps?: UspsResult;
  /** Exact payload the push step sends to PE for this row. */
  updateRecord: Record<string, string>;
  /** False when nothing changed or ClientCode is missing. */
  pushable: boolean;
}

export interface CleanseSummary {
  total: number;
  changed: number;
  unchanged: number;
  pushable: number;
  withWarnings: number;
  uspsAvailable: boolean;
  uspsActive: boolean;
  uspsVerified: number;
  uspsFailed: number;
}

export interface CleanseResponse {
  summary: CleanseSummary;
  fieldKeys: string[];
  results: CleansedRow[];
}

/**
 * Cleanse a batch of rows. Each row needs ClientCode plus any subset of the
 * cleanse fields; fields omitted from a row are left untouched (never blanked).
 */
export async function cleanseAddresses(
  rows: Record<string, string>[],
  useUsps = true,
): Promise<CleanseResponse> {
  const res = await fetch('/api/address-cleanse', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ rows, useUsps }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }

  return res.json();
}

/** Human-readable label + tone for a USPS status chip. */
export function uspsStatusLabel(status: UspsStatus): { label: string; tone: 'ok' | 'warn' | 'bad' | 'muted' } {
  switch (status) {
    case 'verified':         return { label: 'USPS verified',    tone: 'ok' };
    case 'missing_unit':     return { label: 'Missing unit',     tone: 'warn' };
    case 'unit_unconfirmed': return { label: 'Unit unconfirmed', tone: 'warn' };
    case 'undeliverable':    return { label: 'Undeliverable',    tone: 'bad' };
    case 'not_found':        return { label: 'Not found',        tone: 'bad' };
    case 'error':            return { label: 'USPS error',       tone: 'bad' };
    case 'quota':            return { label: 'Quota exceeded',   tone: 'warn' };
    case 'skipped':          return { label: 'Skipped',          tone: 'muted' };
    case 'unverified':       return { label: 'Unverified',       tone: 'muted' };
    case 'disabled':         return { label: 'USPS off',         tone: 'muted' };
    default:                 return { label: String(status),     tone: 'muted' };
  }
}
